// src/components/Layout/Dashboard/Restaurant/RestaurantOrderHistory.jsx
import { useState, useEffect, useContext } from "react";
import { postData } from "../../../../services/Fetchnodeservices";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../../../context/ThemeContext"; // 👈 Dynamic Theme Import

// Icons
const SpinnerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" style={{ animation: "spin 0.8s linear infinite", transformOrigin: "center" }}/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const OrderStatusBadge = ({ status, theme }) => {
  const statusMap = {
    delivered: { bg: `${theme.success}1a`, color: theme.success, text: "Delivered" },
    completed: { bg: `${theme.success}1a`, color: theme.success, text: "Completed" },
    pending: { bg: `${theme.warning}1a`, color: theme.warning, text: "Pending" },
    placed: { bg: `${theme.primary}1a`, color: theme.primary, text: "Placed" },
    accepted: { bg: `${theme.primary}1a`, color: theme.primary, text: "Accepted" },
    processing: { bg: `${theme.primary}1a`, color: theme.primary, text: "Processing" },
    cancelled: { bg: `${theme.error}1a`, color: theme.error, text: "Cancelled" },
    confirmed: { bg: `${theme.primary}1a`, color: theme.primary, text: "Confirmed" },
    shipped: { bg: `${theme.info}1a`, color: theme.info, text: "Shipped" }
  };
  const c = statusMap[status?.toLowerCase()] || { bg: `${theme.textMuted}1a`, color: theme.textMuted, text: status || "Unknown" };
  return (
    <span style={{ fontSize: "11px", fontWeight: 600, padding: "4px 10px", borderRadius: "12px", background: c.bg, color: c.color }}>
      {c.text}
    </span>
  );
};

const RestaurantOrderHistory = ({ onClose, isDark = true, restaurantId, restaurantName }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext); // 👈 Get dynamic theme
  
  const getDesignationId = () => {
    if (location.state?.designation_id) return location.state.designation_id;
    if (restaurantId) return restaurantId;
    const stored = sessionStorage.getItem('selectedRestaurant');
    if (stored) {
      try { return JSON.parse(stored).designation_id; } catch (e) { console.error("Error parsing stored data:", e); }
    }
    const urlParams = new URLSearchParams(window.location.search);
    const urlId = urlParams.get('designation_id');
    if (urlId) return urlId;
    return null;
  };
  
  const getRestaurantNameFromSources = () => {
    if (location.state?.restaurantName) return location.state.restaurantName;
    if (restaurantName) return restaurantName;
    const stored = sessionStorage.getItem('selectedRestaurant');
    if (stored) {
      try { return JSON.parse(stored).restaurantName; } catch (e) { console.error("Error parsing stored data:", e); }
    }
    const urlParams = new URLSearchParams(window.location.search);
    const urlName = urlParams.get('restaurant_name');
    if (urlName) return decodeURIComponent(urlName);
    return null;
  };
  
  const designation_id = getDesignationId();
  const passedRestaurantName = getRestaurantNameFromSources();
  
  useEffect(() => {
    if (designation_id) {
      sessionStorage.setItem('selectedRestaurant', JSON.stringify({
        designation_id: designation_id,
        restaurantName: passedRestaurantName
      }));
    }
  }, [designation_id, passedRestaurantName]);

  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [restaurantInfo, setRestaurantInfo] = useState({ name: passedRestaurantName, id: designation_id });

  useEffect(() => {
    if (!designation_id) {
      setError("No restaurant selected. Please go back and select a restaurant.");
      setLoading(false);
    } else {
      fetchOrders();
      fetchRestaurantInfo();
    }
  }, [designation_id]);

  const fetchRestaurantInfo = async () => {
    try {
      const response = await postData("restaurant/ordersbydesignation_id", { designation_id });
      if (response?.status && response?.data) {
        setRestaurantInfo({
          name: response.data.organisation_name || response.data.name || passedRestaurantName,
          id: designation_id
        });
      }
    } catch (err) {
      console.error("Error fetching restaurant info:", err);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    if (!designation_id) {
      setError("No restaurant ID available");
      setLoading(false);
      return;
    }
    try {
      const response = await postData("restaurant/ordersbydesignation_id", { designation_id });
      let ordersData = [];
      if (response?.status === true && Array.isArray(response?.data)) ordersData = response.data;
      else if (Array.isArray(response)) ordersData = response;
      else if (response?.data && Array.isArray(response.data)) ordersData = response.data;
      else if (response?.orders && Array.isArray(response.orders)) ordersData = response.orders;
      
      setAllOrders(ordersData);
      setOrders(ordersData);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(err.message || "Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onClose) onClose();
    else navigate(-1);
  };

  const filterOrders = (type) => {
    setActiveTab(type);
    if (type === "all") {
      setOrders(allOrders);
    } else if (type === "delivered") {
      setOrders(allOrders.filter(isDelivered));
    } else if (type === "pending") {
      setOrders(allOrders.filter(o => {
        return !isDelivered(o) && !isCancelled(o);
      }));
    } else if (type === "cancelled") {
      setOrders(allOrders.filter(isCancelled));
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getItemsList = (itemsStr) => {
    try {
      const items = JSON.parse(itemsStr);
      if (Array.isArray(items)) return items.map(item => `${item.name} x${item.qty}`).join(", ");
      return itemsStr;
    } catch {
      return itemsStr || "-";
    }
  };

  const getItemsArray = (itemsStr) => {
    try {
      const items = JSON.parse(itemsStr);
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  };

  const getCustomerName = (order) => {
    if (order.user_name) return order.user_name;
    if (order.customer_name) return order.customer_name;
    if (order.receiver_name) return order.receiver_name;
    return `Customer ${order.user_id || "N/A"}`;
  };

  const isDelivered = order => [order.order_status_user, order.order_status_driver].some(status => String(status || "").toLowerCase() === "delivered");
  const isCancelled = order => [order.order_status_user, order.order_status_restro, order.order_status_driver].some(status => ["cancelled", "declined"].includes(String(status || "").toLowerCase()));

  const stats = {
    total: allOrders.length,
    delivered: allOrders.filter(isDelivered).length,
    pending: allOrders.filter(o => !isDelivered(o) && !isCancelled(o)).length,
    cancelled: allOrders.filter(isCancelled).length,
    totalRevenue: allOrders.filter(isDelivered).reduce((sum, order) => sum + (parseFloat(order.business_amount) || 0), 0)
  };

  const ViewOrderModal = ({ order, onClose }) => {
    return (
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)", zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: "20px"
      }} onClick={onClose}>
        <div style={{
          background: theme.surface, borderRadius: "16px", maxWidth: "750px", width: "100%",
          maxHeight: "85vh", overflow: "auto", border: `1px solid ${theme.border}`
        }} onClick={e => e.stopPropagation()}>
          <div style={{
            padding: "20px", borderBottom: `1px solid ${theme.border}`, display: "flex",
            justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0,
            background: theme.surface, zIndex: 1
          }}>
            <h3 style={{ margin: 0, color: theme.textPrimary }}>Order Details #{order.id}</h3>
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: theme.textMuted }}>×</button>
          </div>
          <div style={{ padding: "20px" }}>
            {/* Restaurant Name Highlighted */}
            <div style={{ 
              background: `linear-gradient(135deg, ${theme.primaryLight} 0%, ${theme.error}1a 100%)`,
              padding: "16px", borderRadius: "12px", marginBottom: "20px", border: `1px solid ${theme.primary}40`, textAlign: "center"
            }}>
              <strong style={{ color: theme.primary, fontSize: "18px" }}>🏪 {restaurantInfo.name || order.restaurant_name || "Restaurant"}</strong>
            </div>

            {/* Customer & Rider Info */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "20px", background: theme.surfaceAlt, padding: "16px", borderRadius: "12px" }}>
              <div>
                <strong style={{ color: theme.textPrimary }}>👤 Customer:</strong>
                <p style={{ margin: "4px 0 0", color: theme.textSecondary }}>{getCustomerName(order)}</p>
                {order.receiver_phone && <p style={{ margin: "4px 0 0", fontSize: "11px", color: theme.textMuted }}>{order.receiver_phone}</p>}
              </div>
              <div>
                <strong style={{ color: theme.textPrimary }}>🛵 Rider:</strong>
                <p style={{ margin: "4px 0 0", color: theme.textSecondary }}>{order.driver_name || order.rider_name || "Not assigned yet"}</p>
              </div>
            </div>

            {/* Order Details */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "20px", background: theme.surfaceAlt, padding: "16px", borderRadius: "12px" }}>
              <div>
                <strong style={{ color: theme.textPrimary }}>Order ID:</strong>
                <p style={{ margin: "4px 0 0", color: theme.textSecondary, fontFamily: "monospace" }}>#{order.id}</p>
              </div>
              <div>
                <strong style={{ color: theme.textPrimary }}>Date & Time:</strong>
                <p style={{ margin: "4px 0 0", color: theme.textSecondary }}>{formatDateTime(order.created_at)}</p>
              </div>
              <div>
                <strong style={{ color: theme.textPrimary }}>Payment Method:</strong>
                <p style={{ margin: "4px 0 0", color: theme.textSecondary }}>{order.pay_method || "N/A"}</p>
              </div>
              <div>
                <strong style={{ color: theme.textPrimary }}>Payment Status:</strong>
                <p style={{ margin: "4px 0 0", color: order.pay_status === "paid" ? theme.success : theme.warning, fontWeight: 600 }}>
                  {order.pay_status === "paid" ? "✓ Paid" : "Pending"}
                </p>
              </div>
            </div>

            {/* Items Details */}
            <div style={{ marginBottom: "20px" }}>
              <strong style={{ color: theme.textPrimary }}>Items Ordered:</strong>
              <div style={{ marginTop: "8px", background: theme.surfaceAlt, padding: "12px", borderRadius: "8px", border: `1px solid ${theme.border}` }}>
                {getItemsArray(order.items).map((item, idx) => (
                  <div key={idx} style={{ display: "flex", justifyContent: "space-between", marginBottom: idx < getItemsArray(order.items).length - 1 ? "12px" : 0, paddingBottom: idx < getItemsArray(order.items).length - 1 ? "12px" : 0, borderBottom: idx < getItemsArray(order.items).length - 1 ? `1px solid ${theme.border}` : "none" }}>
                    <div>
                      <span style={{ color: theme.textPrimary, fontWeight: 500 }}>{item.name}</span>
                      {item.variant_name && <span style={{ color: theme.textMuted, fontSize: "11px", marginLeft: "8px" }}>({item.variant_name})</span>}
                      <div style={{ color: theme.textMuted, fontSize: "11px", marginTop: "4px" }}>Qty: {item.qty} × ₹{item.price}</div>
                    </div>
                    <span style={{ color: theme.textPrimary, fontWeight: 600 }}>₹{item.price * item.qty}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Breakdown */}
            <div style={{ marginBottom: "20px" }}>
              <strong style={{ color: theme.textPrimary }}>Price Breakdown:</strong>
              <div style={{ marginTop: "8px", background: theme.surfaceAlt, padding: "16px", borderRadius: "8px", border: `1px solid ${theme.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}><span style={{ color: theme.textSecondary }}>Item Total:</span><span style={{ color: theme.textPrimary }}>₹{order.price || 0}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}><span style={{ color: theme.textSecondary }}>Delivery Charge:</span><span style={{ color: theme.textPrimary }}>₹{order.delivery_charge || 0}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}><span style={{ color: theme.textSecondary }}>Tax:</span><span style={{ color: theme.textPrimary }}>₹{order.tax || 0}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}><span style={{ color: theme.textSecondary }}>Platform Fee:</span><span style={{ color: theme.textPrimary }}>₹{order.user_platform_fee || order.business_platform_fee || 0}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}><span style={{ color: theme.textSecondary }}>Handling Charge:</span><span style={{ color: theme.textPrimary }}>₹{order.handling_charge || 0}</span></div>
                {order.discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}><span style={{ color: theme.textSecondary }}>Discount:</span><span style={{ color: theme.success }}>-₹{order.discount}</span></div>}
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "12px", marginTop: "8px", borderTop: `1px solid ${theme.border}` }}>
                  <strong style={{ color: theme.textPrimary, fontSize: "16px" }}>Total Amount:</strong>
                  <strong style={{ color: theme.primary, fontSize: "18px" }}>₹{order.final_amount || order.amount || 0}</strong>
                </div>
              </div>
            </div>

            {/* Order Timeline */}
            <div>
              <strong style={{ color: theme.textPrimary }}>Order Timeline:</strong>
              <div style={{ marginTop: "8px", background: theme.surfaceAlt, padding: "16px", borderRadius: "8px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${theme.border}` }}><span style={{ color: theme.textMuted }}>📅 Order Placed</span><span style={{ color: theme.textSecondary }}>{formatDateTime(order.created_at)}</span></div>
                {order.restro_accept_time && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${theme.border}` }}><span style={{ color: theme.textMuted }}>✅ Restaurant Accepted</span><span style={{ color: theme.textSecondary }}>{formatDateTime(order.restro_accept_time)}</span></div>}
                {order.restro_ready_time && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${theme.border}` }}><span style={{ color: theme.textMuted }}>🍽️ Restaurant Ready</span><span style={{ color: theme.textSecondary }}>{formatDateTime(order.restro_ready_time)}</span></div>}
                {order.rider_pickup_time && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", paddingBottom: "8px", borderBottom: `1px solid ${theme.border}` }}><span style={{ color: theme.textMuted }}>🛵 Rider Pickup</span><span style={{ color: theme.textSecondary }}>{formatDateTime(order.rider_pickup_time)}</span></div>}
                {order.order_delivered_time && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: theme.textMuted }}>🎯 Delivered</span><span style={{ color: theme.success, fontWeight: 600 }}>{formatDateTime(order.order_delivered_time)}</span></div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: "100vh", background: theme.background, fontFamily: "'DM Sans', sans-serif", padding: "20px 24px" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .order-container { animation: slideIn 0.3s ease; }
      `}</style>

      <div className="order-container">
        {/* Enhanced Header with Restaurant Info */}
        <div style={{ 
          background: `linear-gradient(135deg, ${theme.surface} 0%, ${theme.primaryLight} 100%)`,
          borderRadius: "16px", padding: "24px", marginBottom: "24px", border: `1px solid ${theme.border}`, boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <button 
                onClick={handleBack}
                style={{
                  display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px",
                  background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "10px",
                  cursor: "pointer", color: theme.textSecondary, fontSize: "13px", fontWeight: 500, transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceAlt}
                onMouseLeave={(e) => e.currentTarget.style.background = theme.surface}
              >
                <ChevronLeftIcon /> Back
              </button>
              
              <div>
                <h1 style={{ margin: 0, fontSize: "28px", fontWeight: 700, color: theme.textPrimary }}>🍽️ Order History</h1>
                <div style={{ marginTop: "8px" }}>
                  <span style={{ 
                    background: theme.primaryGradient, color: "#fff",
                    padding: "6px 16px", borderRadius: "20px", fontSize: "14px", fontWeight: 600, display: "inline-block"
                  }}>
                    {restaurantInfo.name || passedRestaurantName || "Restaurant"}
                  </span>
                  <span style={{ marginLeft: "12px", fontSize: "12px", color: theme.textMuted, fontFamily: "monospace" }}>
                    ID: {designation_id}
                  </span>
                </div>
              </div>
            </div>
            
            <div style={{ display: "flex", gap: "12px", background: theme.surfaceAlt, padding: "12px 20px", borderRadius: "12px", border: `1px solid ${theme.border}` }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "24px", fontWeight: 800, color: theme.primary }}>{stats.total}</div>
                <div style={{ fontSize: "11px", color: theme.textMuted }}>Total Orders</div>
              </div>
              <div style={{ width: "1px", background: theme.border }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "24px", fontWeight: 800, color: theme.success }}>₹{stats.totalRevenue.toLocaleString()}</div>
                <div style={{ fontSize: "11px", color: theme.textMuted }}>Restaurant Earnings</div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ background: `${theme.error}1a`, border: `1px solid ${theme.error}40`, borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "13px", color: theme.error }}>⚠ {error}</span>
            <button onClick={() => window.location.reload()} style={{ padding: "4px 12px", borderRadius: "6px", border: "none", background: theme.surface, color: theme.textPrimary, cursor: "pointer", fontSize: "12px" }}>Retry</button>
          </div>
        )}

        {!designation_id && !error && (
          <div style={{ textAlign: "center", padding: "80px 20px", background: theme.surface, borderRadius: "16px", border: `1px solid ${theme.border}` }}>
            <div style={{ fontSize: "80px", marginBottom: "20px" }}>⚠️</div>
            <h3 style={{ margin: "0 0 12px", color: theme.textPrimary, fontSize: "20px" }}>No Restaurant Selected</h3>
            <p style={{ margin: 0, fontSize: "14px", color: theme.textMuted }}>Please go back and select a restaurant to view orders</p>
            <button onClick={handleBack} style={{ marginTop: "20px", padding: "10px 24px", background: theme.primary, border: "none", borderRadius: "8px", color: "#fff", cursor: "pointer", fontSize: "14px" }}>Go Back</button>
          </div>
        )}

        {loading && designation_id && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 20px", background: theme.surface, borderRadius: "16px", border: `1px solid ${theme.border}` }}>
            <SpinnerIcon />
            <p style={{ marginTop: "16px", color: theme.textMuted }}>Loading orders...</p>
          </div>
        )}

        {!loading && !error && designation_id && (
          stats.total > 0 ? (
            <>
              {/* Stats Cards Row */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
                <div style={{ background: theme.surface, borderRadius: "12px", padding: "16px", border: `1px solid ${theme.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: 800, color: theme.primary }}>{stats.total}</div>
                  <div style={{ fontSize: "12px", color: theme.textMuted }}>Total Orders</div>
                </div>
                <div style={{ background: theme.surface, borderRadius: "12px", padding: "16px", border: `1px solid ${theme.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: 800, color: theme.success }}>{stats.delivered}</div>
                  <div style={{ fontSize: "12px", color: theme.textMuted }}>Delivered</div>
                </div>
                <div style={{ background: theme.surface, borderRadius: "12px", padding: "16px", border: `1px solid ${theme.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: 800, color: theme.warning }}>{stats.pending}</div>
                  <div style={{ fontSize: "12px", color: theme.textMuted }}>Pending</div>
                </div>
                <div style={{ background: theme.surface, borderRadius: "12px", padding: "16px", border: `1px solid ${theme.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: 800, color: theme.error }}>{stats.cancelled}</div>
                  <div style={{ fontSize: "12px", color: theme.textMuted }}>Cancelled</div>
                </div>
              </div>

              {/* Filter Tabs */}
              <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap", borderBottom: `1px solid ${theme.border}`, paddingBottom: "12px" }}>
                {[
                  { key: "all", label: "All Orders", count: stats.total, color: theme.primary },
                  { key: "delivered", label: "Delivered", count: stats.delivered, color: theme.success },
                  { key: "pending", label: "Pending", count: stats.pending, color: theme.warning },
                  { key: "cancelled", label: "Cancelled", count: stats.cancelled, color: theme.error }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => filterOrders(tab.key)}
                    style={{
                      background: activeTab === tab.key ? tab.color : "transparent",
                      color: activeTab === tab.key ? "#fff" : theme.textSecondary,
                      border: activeTab === tab.key ? "none" : `1px solid ${theme.border}`,
                      padding: "8px 20px", borderRadius: "24px", fontSize: "13px", fontWeight: 500, cursor: "pointer", transition: "all 0.2s"
                    }}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </div>

              {/* Orders Table */}
              <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "12px", overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1100px" }}>
                    <thead>
                      <tr style={{ background: theme.surfaceAlt }}>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Order ID</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Date & Time</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Customer</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Items</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Amount</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Payment</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Status</th>
                        <th style={{ padding: "14px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}` }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, idx) => (
                        <tr
                          key={order.id || idx}
                          style={{ borderBottom: idx < orders.length - 1 ? `1px solid ${theme.border}` : "none", transition: "background 0.2s ease" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = theme.surfaceAlt)}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <td style={{ padding: "12px 14px", fontSize: "13px", fontWeight: 600, color: theme.textPrimary, fontFamily: "monospace" }}>#{order.id}</td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: theme.textMuted }}>{formatDateTime(order.created_at)}</td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: theme.textSecondary }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <div style={{
                                width: "28px", height: "28px", borderRadius: "50%",
                                background: theme.primaryGradient,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "12px", fontWeight: 600, color: "#fff"
                              }}>
                                {getCustomerName(order).charAt(0).toUpperCase()}
                              </div>
                              <strong>{getCustomerName(order)}</strong>
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: theme.textSecondary }}>{getItemsList(order.items)}</td>
                          <td style={{ padding: "12px 14px", fontSize: "14px", fontWeight: 700, color: theme.textPrimary }}>₹{order.final_amount || order.amount || 0}</td>
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: theme.textSecondary }}>
                            <span style={{
                              display: "inline-block", padding: "2px 8px", borderRadius: "4px",
                              background: order.pay_method === "COD" ? theme.warningLight : theme.successLight,
                              color: order.pay_method === "COD" ? theme.warning : theme.success
                            }}>
                              {order.pay_method === "COD" ? "💵 COD" : "💳 Online"}
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px" }}><OrderStatusBadge status={order.order_status_restro || order.status} theme={theme} /></td>
                          <td style={{ padding: "12px 14px" }}>
                            <button
                              onClick={() => setSelectedOrder(order)}
                              style={{
                                background: theme.primaryLight, border: `1px solid ${theme.primary}40`,
                                cursor: "pointer", color: theme.primary, display: "flex", alignItems: "center",
                                gap: "6px", fontSize: "12px", padding: "6px 12px", borderRadius: "6px", transition: "all 0.2s"
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = theme.primary; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = theme.primaryLight; e.currentTarget.style.color = theme.primary; }}
                            >
                              <EyeIcon /> View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "80px 20px", background: theme.surface, borderRadius: "16px", border: `1px solid ${theme.border}` }}>
              <div style={{ fontSize: "80px", marginBottom: "20px" }}>🏪</div>
              <h3 style={{ margin: "0 0 12px", color: theme.textPrimary, fontSize: "20px" }}>No Orders Found</h3>
              <p style={{ margin: 0, fontSize: "14px", color: theme.textMuted }}>This restaurant hasn't received any orders yet</p>
            </div>
          )
        )}
      </div>

      {selectedOrder && <ViewOrderModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
    </div>
  );
};

export default RestaurantOrderHistory;
